// Samlad nedladdning av alla Word-dokument i projektet som en ZIP.
// Strukturen följer mappträdet: Allmänt först, sedan en mapp per etapp.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Download, Folder, Loader2 } from "lucide-react";
import {
  collectExportGroups,
  downloadAllDocuments,
  PHASE_LABELS,
  ROOT_FOLDER_KEY,
  type ExportDocGroup,
  type ExportFolder,
  type ExportPhase,
  type FullExportProgress,
} from "@/lib/project/fullDocumentExport";
import BackupPanel from "@/components/Export/BackupPanel";

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PHASE_ORDER: ExportPhase[] = [
  "01_Planering",
  "02_Projektering",
  "03_Utforande",
  "04_Bruksskede",
];

const DownloadAllDialog: React.FC<Props> = ({ projectId, open, onOpenChange }) => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<ExportDocGroup[]>([]);
  const [folders, setFolders] = useState<ExportFolder[]>([]);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [includeRaw, setIncludeRaw] = useState(true);
  const [progress, setProgress] = useState<FullExportProgress | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await collectExportGroups(projectId);
      setGroups(result.groups);
      setFolders(result.folders);
      setProjectName(result.projectName);
      setSelected(result.groups.filter((g) => g.count > 0).map((g) => g.key));
    } catch {
      toast.error("Kunde inte läsa projektets dokument");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggle = (key: string) =>
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const groupsInFolder = (folderKey: string) =>
    groups.filter((g) => g.folderKey === folderKey);

  const toggleFolder = (folderKey: string, checked: boolean) => {
    const keys = groupsInFolder(folderKey)
      .filter((g) => g.count > 0)
      .map((g) => g.key);
    setSelected((prev) =>
      checked
        ? Array.from(new Set([...prev, ...keys]))
        : prev.filter((k) => !keys.includes(k))
    );
  };

  const totalFiles = groups
    .filter((g) => g.count > 0 && selected.includes(g.key))
    .reduce((sum, g) => sum + g.count, 0);

  const handleDownload = async () => {
    setProgress({ current: 0, total: selected.length, label: "Startar…" });
    try {
      const result = await downloadAllDocuments(projectId, {
        groups,
        folders,
        selectedKeys: selected,
        projectName,
        includeRawData: includeRaw,
        onProgress: setProgress,
      });
      if (result.failed.length) {
        toast.warning(
          `${result.fileCount} filer nedladdade. ${result.failed.length} dokument kunde inte skapas.`
        );
      } else {
        toast.success(`${result.fileCount} Word-dokument nedladdade som ZIP`);
      }
      onOpenChange(false);
    } catch {
      toast.error("Kunde inte skapa nedladdningen");
    } finally {
      setProgress(null);
    }
  };

  const busy = progress !== null;

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ladda ner alla dokument</DialogTitle>
          <DialogDescription>
            Paketet följer projektets mappträd: Allmänt högst upp och en mapp per etapp.
            Välj själv vilka dokument som ska följa med.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Läser projektet…</p>
        ) : (
          <ScrollArea className="max-h-[45vh] pr-3">
            <div className="space-y-6">
              {folders.map((folder) => {
                const folderGroups = groupsInFolder(folder.key);
                const available = folderGroups.filter((g) => g.count > 0);
                if (!folderGroups.length) return null;
                const allSelected =
                  available.length > 0 &&
                  available.every((g) => selected.includes(g.key));
                const someSelected = available.some((g) => selected.includes(g.key));
                const fileTotal = available.reduce((sum, g) => sum + g.count, 0);

                return (
                  <div key={folder.key} className="space-y-2">
                    <div className="flex items-center justify-between gap-3 rounded-md bg-muted/60 px-3 py-2">
                      <label className="flex cursor-pointer items-center gap-3">
                        <Checkbox
                          checked={allSelected ? true : someSelected ? "indeterminate" : false}
                          disabled={busy || !available.length}
                          onCheckedChange={(v) => toggleFolder(folder.key, Boolean(v))}
                        />
                        <span className="flex items-center gap-2 text-sm font-semibold">
                          <Folder className="h-4 w-4 text-muted-foreground" />
                          {folder.key === ROOT_FOLDER_KEY ? folder.name : folder.name}
                        </span>
                      </label>
                      <Badge variant={fileTotal ? "secondary" : "outline"}>
                        {fileTotal ? `${fileTotal} dokument` : "tomt"}
                      </Badge>
                    </div>

                    {PHASE_ORDER.map((phase) => {
                      const phaseGroups = folderGroups.filter((g) => g.phase === phase);
                      if (!phaseGroups.length) return null;
                      return (
                        <div key={phase} className="space-y-2 pl-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {PHASE_LABELS[phase]}
                          </p>
                          {phaseGroups.map((group) => (
                            <label
                              key={group.key}
                              className={`flex items-center justify-between gap-3 rounded-md border p-3 ${
                                group.count === 0 ? "opacity-50" : "cursor-pointer"
                              }`}
                            >
                              <span className="flex items-center gap-3">
                                <Checkbox
                                  checked={selected.includes(group.key)}
                                  disabled={group.count === 0 || busy}
                                  onCheckedChange={() => toggle(group.key)}
                                />
                                <span className="text-sm font-medium">
                                  Dok {group.docNumber} · {group.title}
                                </span>
                              </span>
                              <Badge variant={group.count ? "secondary" : "outline"}>
                                {group.count ? `${group.count} st` : "saknas"}
                              </Badge>
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              <label className="flex cursor-pointer items-center gap-3 rounded-md border p-3">
                <Checkbox
                  checked={includeRaw}
                  disabled={busy}
                  onCheckedChange={(v) => setIncludeRaw(Boolean(v))}
                />
                <span className="text-sm">
                  Inkludera underlag (JSON och CSV med all projektdata)
                </span>
              </label>

              <BackupPanel projectId={projectId} compact />

            </div>
          </ScrollArea>
        )}

        {busy && (
          <div className="space-y-2">
            <Progress value={(progress.current / Math.max(progress.total, 1)) * 100} />
            <p className="text-xs text-muted-foreground">
              {progress.current} av {progress.total} · {progress.label}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Avbryt
          </Button>
          <Button onClick={handleDownload} disabled={busy || loading || !selected.length}>
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {busy ? "Skapar ZIP…" : `Ladda ner (${totalFiles} filer)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadAllDialog;
